import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Component that persists config URL parameters across route changes.
 * Place this inside the Router to automatically restore apiUrl, wsUrl, kioskId
 * params when navigation would otherwise lose them.
 */
export function ConfigParamsPersist() {
  const location = useLocation();
  const navigate = useNavigate();
  const configParamsRef = useRef<string>('');

  // Capture config params on initial load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const apiUrl = params.get('apiUrl');
    const wsUrl = params.get('wsUrl');
    const kioskId = params.get('kioskId');

    // Build config params string
    const configParams = new URLSearchParams();
    if (apiUrl) configParams.set('apiUrl', apiUrl);
    if (wsUrl) configParams.set('wsUrl', wsUrl);
    if (kioskId) configParams.set('kioskId', kioskId);

    const paramsStr = configParams.toString();
    if (paramsStr) {
      configParamsRef.current = paramsStr;
    }
  }, []); // Only on mount

  // Restore config params if they're missing after navigation
  useEffect(() => {
    if (!configParamsRef.current) return;

    const currentParams = new URLSearchParams(location.search);
    const hasConfigParams = currentParams.has('apiUrl') || currentParams.has('wsUrl') || currentParams.has('kioskId');

    if (!hasConfigParams) {
      // Config params are missing - restore them
      const newSearch = location.search
        ? `${location.search}&${configParamsRef.current}`
        : `?${configParamsRef.current}`;

      navigate(
        { pathname: location.pathname, search: newSearch, hash: location.hash },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}

export default ConfigParamsPersist;
